import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import Footer from "@/components/Footer";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const motivo = new URLSearchParams(window.location.search).get("motivo");
  const otraSesion = motivo === "otra_sesion";
  const passwordRestablecida = motivo === "password_restablecida";

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white text-slate-500">Cargando…</div>;
  if (user) return <Navigate to={user.role === "empleado" ? "/app/portal" : "/app/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email.trim().toLowerCase(), password);
      navigate(u.role === "empleado" ? "/app/portal" : "/app/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex lg:col-span-2 flex-col items-start justify-center p-10 bg-brand text-white relative overflow-hidden">
        <img src="/brand/monograma-blanco.png" alt="" aria-hidden="true" className="absolute -right-24 -bottom-24 w-[28rem] opacity-[0.06] pointer-events-none select-none" />
        <div className="relative z-10">
          <img
            src="/brand/logotipo-blanco.png"
            alt="CHKR — Jornada Electrónica"
            className="h-16 w-auto"
          />
        </div>
      </div>

      {/* Right: login form */}
      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src="/brand/logotipo.png" alt="CHKR" className="h-10 w-auto mb-8 lg:hidden" />
            <div className="label-uppercase mb-3 flex items-center gap-2">
              <ShieldCheck size={14} weight="bold" /> Acceso seguro
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-ink">
              Iniciar sesión
            </h1>
            <p className="mt-2 text-slate-600">Ingresa con tus credenciales corporativas.</p>
          </div>

          {otraSesion && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md mb-5" data-testid="login-otra-sesion">
              Tu sesión se cerró porque iniciaste sesión en otro dispositivo. Solo puedes tener un dispositivo activo a la vez.
            </div>
          )}
          {passwordRestablecida && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md mb-5" data-testid="login-password-restablecida">
              Tu contraseña se actualizó correctamente. Inicia sesión con tu nueva contraseña.
            </div>
          )}
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-uppercase">Correo</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.mx"
                required
                data-testid="login-email"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="label-uppercase">Contraseña</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-brand-accent hover:text-brand" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold"
              data-testid="login-submit"
            >
              {busy ? "Verificando…" : "Entrar"}
            </Button>
          </form>
          <Footer className="mt-10" />
        </div>
      </div>
    </div>
  );
}
